//! Pagina dettaglio market in stile Polymarket.
//! Layout 2-col: contenuto principale a sinistra + sticky trade panel a destra.

import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { Pill } from "@/components/atoms/Pill";
import { Segmented } from "@/components/atoms/Segmented";
import {
  fmtMarketMetric,
  fmtShortDate,
  parseDecimal,
} from "@/components/atoms/format";
import { marketMetrics, type MarketWithMetrics } from "@/api/marketMetrics";
import { BigChart } from "@/components/market/BigChart";
import { OrderBook } from "@/components/market/OrderBook";
import { ResolutionPanel } from "@/components/market/ResolutionPanel";
import { useMid } from "@/components/market/useMid";
import { useOhlc, type Bucket, type Range as ApiRange } from "@/components/market/useOhlc";
import { InlineTradePanel } from "@/components/trade/InlineTradePanel";
import type { components } from "@/api/schema.gen";

type MarketView = MarketWithMetrics;
type OrderBookView = components["schemas"]["OrderBookView"];
type TradeView = components["schemas"]["TradeView"];
type OutcomeView = components["schemas"]["OutcomeView"];

type Tab = "orderbook" | "chart" | "resolution";
type Range = "1h" | "1d" | "1w" | "1m" | "all";

const RANGES: { value: Range; label: string }[] = [
  { value: "1h", label: "1H" },
  { value: "1d", label: "1D" },
  { value: "1w", label: "1W" },
  { value: "1m", label: "1M" },
  { value: "all", label: "ALL" },
];

const CATEGORY_DOTS: Record<string, string> = {
  crypto: "#F7A23B",
  politics: "#5B7FFF",
  sports: "#2EE6A8",
  tech: "#8AA6FF",
  science: "#5B7FFF",
  culture: "#FF6B6B",
  macro: "#C5CEDB",
};

export function MarketDetailView() {
  const slug = useStore((s) => s.detailSlug);
  const openDetail = useStore((s) => s.openDetail);
  const setView = useStore((s) => s.setView);
  const [market, setMarket] = useState<MarketView | null>(null);
  const [activeOutcome, setActiveOutcome] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await api.GET("/v1/markets/{slug}", {
        params: { path: { slug } },
      });
      if (cancelled || !data) return;
      setMarket(data);
      setActiveOutcome((prev) => prev ?? data.outcomes[0]?.id ?? null);
    };
    load();
    const t = setInterval(load, 8_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [slug]);

  if (!slug) {
    return null;
  }
  if (!market) {
    return (
      <div className="px-4 pb-28 pt-4 sm:px-6 lg:pb-20">
        <div className="card h-[420px] animate-pulse" />
      </div>
    );
  }

  const isMulti = market.outcomes.length > 2;
  const selectedOutcome =
    market.outcomes.find((o) => o.id === activeOutcome) ?? market.outcomes[0];

  return (
    <div className="px-4 pb-28 pt-4 sm:px-6 lg:pb-20">
      {/* Breadcrumb */}
      <button
        className="mb-4 flex items-center gap-1 text-[12px] text-muted hover:text-text-2"
        onClick={() => {
          openDetail(null);
          setView("markets");
        }}
      >
        <Icon.ChevronLeft size={14} />
        Back to markets
      </button>

      {/* `minmax(0,…)` e non `1fr`: una traccia auto non scende sotto il
          min-content del contenuto, e le righe outcome la spingevano a 472px
          dentro un contenitore da 343, sfondando il viewport. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Main column */}
        <div className="flex flex-col gap-4">
          <MarketHeader market={market} />

          {isMulti ? (
            <MultiOutcomeList
              market={market}
              active={activeOutcome}
              onSelect={(id) => setActiveOutcome(id)}
            />
          ) : (
            <BinaryProbabilityRow market={market} />
          )}

          <MarketTabs
            market={market}
            outcomeId={selectedOutcome?.id ?? null}
          />
        </div>

        {/* Right sticky trade panel */}
        <div className="lg:sticky lg:top-[72px] self-start">
          {selectedOutcome && (
            <InlineTradePanel
              market={market}
              outcomeId={selectedOutcome.id}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── header ──────────────────────────────────────────────────────────

function MarketHeader({ market }: { market: MarketView }) {
  const dot = CATEGORY_DOTS[market.category] ?? "#5B7FFF";
  const { volume, liquidity } = marketMetrics(market);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted">
        <span className="h-2 w-2 rounded-full" style={{ background: dot }} />
        <span>{market.category}</span>
        <span className="text-line-strong">·</span>
        <span>{market.resolution_source}</span>
      </div>
      <h1 className="text-[26px] font-semibold leading-tight">
        {market.title}
      </h1>
      <div className="flex items-center gap-4 text-[12px] text-muted">
        <span className="flex items-center gap-1 font-mono text-text-2">
          {fmtMarketMetric(volume)} Vol.
        </span>
        <span className="text-line-strong">|</span>
        <span className="flex items-center gap-1 font-mono text-text-2">
          {fmtMarketMetric(liquidity)} Liq.
        </span>
        <span className="text-line-strong">|</span>
        <span className="flex items-center gap-1">
          <Icon.Clock size={12} />
          {fmtShortDate(market.resolve_after)}
        </span>
        {market.status === "open" && <Pill variant="accent">OPEN</Pill>}
        {market.status === "resolved" && <Pill variant="yes">RESOLVED</Pill>}
        {market.status === "closed" && <Pill variant="warn">CLOSED</Pill>}
      </div>
    </div>
  );
}

// ─── binary YES/NO row ───────────────────────────────────────────────

function BinaryProbabilityRow({ market }: { market: MarketView }) {
  const openTrade = useStore((s) => s.openTrade);
  const mid = useMid(market.slug, 4000);
  const yesOutcome = market.outcomes[0];
  const noOutcome = market.outcomes[1];
  return (
    <div className="card flex items-center gap-6 p-5">
      <div className="flex-1">
        <div className="text-[11px] uppercase tracking-wider text-muted">
          YES probability
        </div>
        <div
          className="font-mono text-[44px] font-semibold leading-none"
          style={{ color: mid >= 0.5 ? "var(--yes)" : "var(--no)" }}
        >
          {(mid * 100).toFixed(0)}%
        </div>
      </div>
      <div className="flex gap-2">
        {yesOutcome && (
          <button
            className="btn-yn yes !px-5 !py-3 !text-[14px]"
            onClick={() => openTrade(market, yesOutcome.id, "buy")}
          >
            <span>Buy YES</span>
            <span className="ml-3 font-mono">{(mid * 100).toFixed(1)}¢</span>
          </button>
        )}
        {noOutcome && (
          <button
            className="btn-yn no !px-5 !py-3 !text-[14px]"
            onClick={() => openTrade(market, noOutcome.id, "buy")}
          >
            <span>Buy NO</span>
            <span className="ml-3 font-mono">
              {((1 - mid) * 100).toFixed(1)}¢
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── multi outcome list ──────────────────────────────────────────────

function MultiOutcomeList({
  market,
  active,
  onSelect,
}: {
  market: MarketView;
  active: string | null;
  onSelect: (outcomeId: string) => void;
}) {
  return (
    <div className="card overflow-hidden p-0">
      {market.outcomes.map((o, i) => (
        <OutcomeRow
          key={o.id}
          market={market}
          outcome={o}
          isFirst={i === 0}
          isActive={o.id === active}
          onSelect={() => onSelect(o.id)}
        />
      ))}
    </div>
  );
}

function OutcomeRow({
  market,
  outcome,
  isFirst,
  isActive,
  onSelect,
}: {
  market: MarketView;
  outcome: OutcomeView;
  isFirst: boolean;
  isActive: boolean;
  onSelect: () => void;
}) {
  const openTrade = useStore((s) => s.openTrade);
  // MVP: ogni outcome ha il SUO orderbook, ma il useMid prende il first per slug.
  // Per ora stima uniforme inversa al numero di outcomes; quando avremo OHLC per
  // outcome il valore esatto arriverà da lì.
  const fair = 1 / Math.max(market.outcomes.length, 1);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-2 border-b border-line px-3 py-3 text-left transition-colors hover:bg-bg-2 sm:gap-4 sm:px-5 sm:py-4 ${
        isActive ? "bg-bg-2" : ""
      }`}
    >
      <div
        className={`h-8 w-8 shrink-0 rounded-full sm:h-9 sm:w-9 ${isActive ? "ring-2 ring-accent" : ""}`}
        style={{
          background: `linear-gradient(135deg, hsl(${(outcome.position * 47) % 360} 65% 55%), hsl(${(outcome.position * 47 + 60) % 360} 65% 45%))`,
        }}
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-[14px] font-medium">{outcome.label}</span>
        {isFirst && (
          <span className="mt-[2px] text-[11px] text-muted">primary</span>
        )}
      </div>
      <span
        className="font-mono text-[17px] font-semibold tabular-nums sm:text-[22px]"
        style={{ color: fair >= 0.5 ? "var(--yes)" : "var(--text)" }}
      >
        {(fair * 100).toFixed(0)}%
      </span>
      <div className="flex gap-1.5 sm:gap-2">
        <button
          className="btn-yn yes !px-2 !py-2 !text-[11px] sm:!px-3 sm:!text-[12px]"
          onClick={(e) => {
            e.stopPropagation();
            openTrade(market, outcome.id, "buy");
          }}
        >
          <span>Sì</span>
          <span className="ml-1 font-mono sm:ml-2">
            {(fair * 100).toFixed(0)}¢
          </span>
        </button>
        <button
          className="btn-yn no !px-2 !py-2 !text-[11px] sm:!px-3 sm:!text-[12px]"
          onClick={(e) => {
            e.stopPropagation();
            openTrade(market, outcome.id, "buy");
          }}
        >
          <span>No</span>
          <span className="ml-1 font-mono sm:ml-2">
            {((1 - fair) * 100).toFixed(0)}¢
          </span>
        </button>
      </div>
    </button>
  );
}

// ─── tabs (orderbook / chart / resolution) ───────────────────────────

function MarketTabs({
  market,
  outcomeId,
}: {
  market: MarketView;
  outcomeId: string | null;
}) {
  const [tab, setTab] = useState<Tab>("orderbook");
  const [book, setBook] = useState<OrderBookView | null>(null);
  const [recent, setRecent] = useState<TradeView[]>([]);
  const [range, setRange] = useState<Range>("1d");

  useEffect(() => {
    if (!outcomeId) return;
    let cancelled = false;
    const load = async () => {
      const ob = await api.GET("/v1/markets/{slug}/orderbook", {
        params: { path: { slug: market.slug }, query: { depth: 20 } },
      });
      const tr = await api.GET("/v1/markets/{slug}/trades", {
        params: { path: { slug: market.slug }, query: { limit: 20 } },
      });
      if (cancelled) return;
      if (ob.data) setBook(ob.data);
      if (tr.data) setRecent(tr.data.items);
    };
    load();
    const t = setInterval(load, 4_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [market.slug, outcomeId]);

  const bucket: Bucket =
    range === "1h" ? "1m" : range === "1d" ? "5m" : range === "1w" ? "1h" : "1d";
  const apiRange: ApiRange = range === "all" ? "all" : (range as ApiRange);
  const { points } = useOhlc(market.slug, bucket, apiRange, 10_000);
  const closes = points.map((p) => p.close);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4 border-b border-line">
        <TabButton
          active={tab === "orderbook"}
          onClick={() => setTab("orderbook")}
        >
          Libro ordini
        </TabButton>
        <TabButton active={tab === "chart"} onClick={() => setTab("chart")}>
          Grafico
        </TabButton>
        <TabButton
          active={tab === "resolution"}
          onClick={() => setTab("resolution")}
        >
          Risoluzione
        </TabButton>
      </div>

      {tab === "orderbook" && <OrderBook book={book} />}

      {tab === "chart" && (
        <div className="flex flex-col gap-3">
          <Segmented options={RANGES} value={range} onChange={setRange} />
          {closes.length >= 2 ? (
            <BigChart values={closes} />
          ) : (
            <div className="flex h-[220px] items-center justify-center rounded-card border border-line bg-bg-2 text-[12.5px] text-muted">
              No price history yet — chart appears after the first trade.
            </div>
          )}
        </div>
      )}

      {tab === "resolution" && <ResolutionPanel market={market} />}

      {/* Recent trades sotto i tabs */}
      {recent.length > 0 && (
        <div className="card p-0">
          <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-muted border-b border-line">
            Recent trades
          </div>
          <ul className="max-h-[200px] overflow-y-auto divide-y divide-line">
            {recent.map((t) => (
              <li
                key={t.id}
                className="grid grid-cols-[60px_1fr_1fr_auto] items-center gap-3 px-4 py-2 text-[12.5px]"
              >
                <span
                  className="font-mono uppercase"
                  style={{
                    color:
                      t.taker_side === "buy" ? "var(--yes)" : "var(--no)",
                  }}
                >
                  {t.taker_side === "buy" ? "▲ BUY" : "▼ SELL"}
                </span>
                <span className="font-mono text-text">
                  {(parseDecimal(t.price) * 100).toFixed(1)}¢
                </span>
                <span className="font-mono text-text-2">
                  {parseDecimal(t.size).toFixed(2)} sh
                </span>
                <span className="text-muted">
                  {new Date(t.created_at).toLocaleTimeString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative pb-2 text-[13px] transition-colors ${
        active ? "font-semibold text-text" : "text-muted hover:text-text-2"
      }`}
    >
      {children}
      {active && (
        <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-accent" />
      )}
    </button>
  );
}
